import React from 'react'

import { isAxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'

import { WebhookListItem } from '@/api/types'
import { useCreateWebhook, useUpdateWebhook } from '@/react-query/api-settings'

const WEBHOOK_EVENT_OPTIONS = [
  { value: 'interview.created', label: 'Interview Created' },
  { value: 'interview.completed', label: 'Interview Completed' },
  { value: 'candidate.created', label: 'Candidate Created' },
  { value: 'candidate.updated', label: 'Candidate Updated' },
]

interface WebhookForm {
  url: string
}

export const WebhookModal = ({
  isOpen,
  onClose,
  webhook,
}: {
  isOpen: boolean
  onClose: () => void
  webhook?: WebhookListItem
}) => {
  const toast = useToast()
  const createWebhook = useCreateWebhook()
  const updateWebhook = useUpdateWebhook()
  const isEditMode = !!webhook

  const [selectedEvents, setSelectedEvents] = React.useState<string[]>(
    webhook?.events || [],
  )
  const [createdSecret, setCreatedSecret] = React.useState<string | null>(null)
  const [eventsError, setEventsError] = React.useState<string | null>(null)

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    setError,
    reset,
  } = useForm<WebhookForm>({
    defaultValues: { url: webhook?.url || '' },
  })

  React.useEffect(() => {
    if (isOpen) {
      reset({ url: webhook?.url || '' })
      setSelectedEvents(webhook?.events || [])
      setCreatedSecret(null)
      setEventsError(null)
    }
  }, [isOpen, webhook, reset])

  const handleClose = () => {
    setCreatedSecret(null)
    setEventsError(null)
    reset()
    onClose()
  }

  const toggleEvent = (event: string) => {
    setEventsError(null)
    setSelectedEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    )
  }

  const onSubmit = async ({ url }: WebhookForm) => {
    if (selectedEvents.length === 0) {
      setEventsError('At least one event type is required')
      return
    }

    try {
      if (isEditMode) {
        await updateWebhook.mutateAsync({
          webhookId: webhook.id,
          url,
          events: selectedEvents,
        })
        handleClose()
      } else {
        const response = await createWebhook.mutateAsync({
          url,
          events: selectedEvents,
        })
        setCreatedSecret(response.webhook.secret)
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.data?.error?.message) {
        setError('url', {
          type: 'manual',
          message: error.response.data.error.message,
        })
      } else {
        setError('url', {
          type: 'manual',
          message: 'Something went wrong',
        })
      }
    }
  }

  const handleCopySecret = async () => {
    if (!createdSecret) return
    try {
      await navigator.clipboard.writeText(createdSecret)
      toast({
        title: 'Signing secret copied',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Please copy the secret manually',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal size={'3xl'} isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay bg="whiteAlpha.600" backdropFilter={'auto'} />
      <ModalContent
        bgColor={'#f5f5f5'}
        boxShadow={'0px 4px 30px 0px rgba(0, 0, 0, 0.25)'}
        rounded={'2.5rem'}
        w={'480px'}
        p={3}
      >
        {createdSecret ? (
          <>
            <ModalHeader>Webhook Created</ModalHeader>
            <ModalCloseButton rounded={'full'} mt={5} mr={5} />
            <ModalBody display={'flex'} flexDirection={'column'}>
              <Box
                bg="orange.50"
                border="1px"
                borderColor="orange.200"
                rounded="md"
                p={3}
                mb={4}
              >
                <Text fontWeight="500" color="orange.800">
                  Copy your signing secret now. You won&apos;t be able to see it
                  again.
                </Text>
              </Box>
              <FormLabel fontWeight={'400'} color={'black'}>
                Signing Secret
              </FormLabel>
              <Input
                value={createdSecret}
                readOnly
                variant={'brand-solid-white'}
                fontFamily="mono"
                fontSize="sm"
              />
            </ModalBody>
            <ModalFooter justifyContent={'flex-start'}>
              <Button
                variant={'brand-solid'}
                onClick={handleCopySecret}
                rounded={'1.25rem'}
              >
                Copy
              </Button>
              <Button
                rounded={'1.25rem'}
                variant="brand-solid-white"
                onClick={handleClose}
              >
                Done
              </Button>
            </ModalFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalHeader>
              {isEditMode ? 'Edit Webhook' : 'Add Webhook'}
            </ModalHeader>
            <ModalCloseButton rounded={'full'} mt={5} mr={5} />
            <ModalBody display={'flex'} flexDirection={'column'}>
              <FormControl isInvalid={Boolean(errors.url)}>
                <FormLabel fontWeight={'400'} htmlFor="url" color={'black'}>
                  URL
                </FormLabel>
                <Input
                  id="url"
                  placeholder="https://example.com/webhook"
                  {...register('url', {
                    required: 'URL is required',
                    pattern: {
                      value: /^https:\/\/.+/,
                      message: 'URL must start with https://',
                    },
                  })}
                  variant={'brand-solid-white'}
                />
                <FormErrorMessage>
                  {errors.url && errors.url.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={Boolean(eventsError)}>
                <FormLabel fontWeight={'400'} color={'black'}>
                  Events
                </FormLabel>
                <VStack align="start" spacing={2}>
                  {WEBHOOK_EVENT_OPTIONS.map((option) => (
                    <Checkbox
                      key={option.value}
                      isChecked={selectedEvents.includes(option.value)}
                      onChange={() => toggleEvent(option.value)}
                    >
                      {option.label}
                    </Checkbox>
                  ))}
                </VStack>
                <FormErrorMessage>{eventsError}</FormErrorMessage>
              </FormControl>
            </ModalBody>
            <ModalFooter justifyContent={'flex-start'}>
              <Button
                type="submit"
                variant={'brand-solid'}
                isLoading={isSubmitting}
                rounded={'1.25rem'}
              >
                {isEditMode ? 'Update' : 'Create'}
              </Button>
              <Button
                rounded={'1.25rem'}
                variant="brand-solid-white"
                onClick={handleClose}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  )
}
